/**
 * PortalModal Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Wrapper component for rendering modals via React Portal outside normal DOM hierarchy.
 * Prevents z-index stacking issues and improves modal layering.
 * 
 * Features:
 * - Portal rendering to document.body
 * - Conditional rendering based on isOpen prop
 * - Proper z-index management
 * - Escape key support (handled by parent)
 * - Background click handling (handled by parent)
 * 
 * Props:
 * - isOpen: boolean (visibility control)
 * - onClose: () => void (close handler)
 * - children: React.ReactNode (modal content)
 * 
 * Dependencies:
 * - React 19.2.4 (portals)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 * - 2026-03-05: Added scrollbar width compensation (padding-right) to prevent layout shift
 * - Previous: Body scroll locked but no scrollbar compensation, causing page jump
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';

interface PortalModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
}

export const PortalModal: React.FC<PortalModalProps> = ({ 
  children, 
  isOpen, 
  onClose,
  className = "" 
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Prevent body scroll when modal is open
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        if (onClose) {
          onClose();
        }
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 z-[9999] ${className}`}
        >
          <div ref={modalRef}>
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
